import { Slot } from '@radix-ui/react-slot';
import cn from 'classnames';
import { forwardRef } from 'react';

import { filterStyleProps } from '../../../utils';
import { flexStyleHandlers, useStyleProps } from '../../../utils/hooks';
import { Box } from '../box';

import type { ForwardedRef, ReactElement } from 'react';

import type { FlexProps } from './flex.types';

/**
 * The component for creating flex layouts.
 *
 * ### Usage
 *
 * ```tsx
 * import { Flex } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return <Flex>Write an example of component usage</Flex>;
 * }
 * ```
 */
const Flex = forwardRef((props: FlexProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    asChild,
    children,
    className,
    display = 'flex',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const style = useStyleProps({ ...props, display }, flexStyleHandlers);

  const Root = asChild ? Slot : 'div';

  return (
    <Box asChild>
      <Root
        {...filterStyleProps(rest, flexStyleHandlers)}
        className={cn('rbui-flex', className)}
        ref={forwardedRef}
        style={style}
      >
        {children}
      </Root>
    </Box>
  );
});

Flex.displayName = 'Flex';

export default Flex;
